import { z } from 'zod';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import {
  type ApplicationDeviceContext,
  ApplicationDeviceContextSchema,
} from '@am3nd-org/pointcraft-types';

import { DEVICE_CONTEXT_STORAGE_KEY } from './storage';
import type { DeviceContextState, DeviceContextFields } from './types';

export * from './types';
export { DEVICE_CONTEXT_STORAGE_KEY } from './storage';

export const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

// Device types are a server-owned, extensible vocabulary. Keep every other
// application-context constraint while allowing a newer server string.
const ApplicationDeviceContextCompatibilitySchema = ApplicationDeviceContextSchema.extend({
  deviceType: z.string().min(1),
});

export function isUuid(value: string | null | undefined): value is string {
  return typeof value === 'string' && UUID_RE.test(value);
}

const initialFields: DeviceContextFields = {
  accountNumber: null,
  businessId: null,
  businessDeviceId: null,
  businessLocationId: null,
  terminalId: null,
  deviceFingerprint: null,
  publicKeyFingerprint: null,
  deviceName: null,
  deviceType: null,
  environment: null,
  lastResolvedAt: null,
};

function normalizeNullableString(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

export function normalizeDeviceContextFields(
  value: Partial<DeviceContextFields> | null | undefined
): DeviceContextFields {
  return {
    accountNumber: normalizeNullableString(value?.accountNumber),
    businessId: normalizeNullableString(value?.businessId),
    businessDeviceId: normalizeNullableString(value?.businessDeviceId),
    businessLocationId: normalizeNullableString(value?.businessLocationId),
    terminalId: normalizeNullableString(value?.terminalId),
    deviceFingerprint: normalizeNullableString(value?.deviceFingerprint),
    publicKeyFingerprint: normalizeNullableString(value?.publicKeyFingerprint),
    deviceName: normalizeNullableString(value?.deviceName),
    deviceType: normalizeNullableString(value?.deviceType),
    environment: normalizeNullableString(value?.environment),
    lastResolvedAt: normalizeNullableString(value?.lastResolvedAt),
  };
}

export function partializeDeviceContextState(state: DeviceContextState): DeviceContextFields {
  return {
    accountNumber: state.accountNumber,
    businessId: state.businessId,
    businessDeviceId: state.businessDeviceId,
    businessLocationId: state.businessLocationId,
    terminalId: state.terminalId,
    deviceFingerprint: state.deviceFingerprint,
    publicKeyFingerprint: state.publicKeyFingerprint,
    deviceName: state.deviceName,
    deviceType: state.deviceType,
    environment: state.environment,
    lastResolvedAt: state.lastResolvedAt,
  };
}

export function toAppDeviceAccountContext(
  context: DeviceContextFields
): ApplicationDeviceContext | null {
  const candidate = {
    ...(context.accountNumber ? { accountNumber: context.accountNumber } : {}),
    businessId: context.businessId,
    businessDeviceId: context.businessDeviceId,
    businessLocationId: context.businessLocationId,
    terminalId: context.terminalId,
    deviceFingerprint: context.deviceFingerprint,
    publicKeyFingerprint: context.publicKeyFingerprint,
    deviceName: context.deviceName,
    deviceType: context.deviceType,
    ...(context.environment ? { environment: context.environment } : {}),
    ...(context.lastResolvedAt ? { lastResolvedAt: context.lastResolvedAt } : {}),
  };

  const parsed = ApplicationDeviceContextCompatibilitySchema.safeParse(candidate);
  return parsed.success ? (parsed.data as ApplicationDeviceContext) : null;
}

export const useDeviceContextStore = create<DeviceContextState>()(
  persist(
    (set, get) => ({
      ...initialFields,
      status: 'idle',
      lastError: null,
      setDeviceContext: (patch) => set((state) => ({ ...state, ...patch })),
      setApplicationDeviceContext: (context) => {
        const parsed = ApplicationDeviceContextCompatibilitySchema.parse(context);
        set(normalizeDeviceContextFields(parsed));
      },
      setStatus: (status, error = null) =>
        set({
          status,
          lastError: error,
          ...(status === 'ready' ? { lastResolvedAt: new Date().toISOString() } : {}),
        }),
      setTerminalId: (terminalId) => set({ terminalId }),
      reset: () =>
        set({
          ...initialFields,
          status: 'idle',
          lastError: null,
        }),
      resetIfBusinessChanged: (businessId) => {
        const nextBusinessId = normalizeNullableString(businessId);
        const currentBusinessId = get().businessId;
        if (!nextBusinessId || !currentBusinessId || nextBusinessId === currentBusinessId) {
          return false;
        }
        get().reset();
        return true;
      },
      isReadyForSales: () => isUuid(get().businessLocationId),
      getApplicationDeviceContext: () =>
        toAppDeviceAccountContext(normalizeDeviceContextFields(get())),
      getAppDeviceAccountContext: () =>
        toAppDeviceAccountContext(normalizeDeviceContextFields(get())),
    }),
    {
      name: DEVICE_CONTEXT_STORAGE_KEY,
      version: 2,
      partialize: partializeDeviceContextState,
      migrate: (persistedState) =>
        normalizeDeviceContextFields(persistedState as Partial<DeviceContextFields>),
    }
  )
);
